// Pause menu (§14.2): Resume, Settings, Locker (lobby and Practice), Spectate (when a ghost), Copy
// Room Link, Leave Room; Practice adds Restart. It never pauses the game. Every in-world control is
// mirrored here so it is one Start / Esc / PAUSE press away on any device: READY in the lobby, the
// host's room settings and START, and REMATCH on the podium.
import { RoomPhase } from '@shared/ids';
import type { LiveScreen } from '../contracts';
import type { UiDeps } from '../deps';
import { roomLink } from '../labels';
import { copyText, el, setHidden, setText } from '../ui';
import { button, setLabel } from '../widgets';

export function create(deps: UiDeps): LiveScreen {
  const room = deps.room;
  const resume = button({ label: 'RESUME', icon: 'play', variant: 'pink', size: 'big', cls: 'wide pause-resume', autofocus: true, onClick: () => deps.nav.back() });
  const ready = button({ label: 'READY UP', icon: 'check', variant: 'mint', cls: 'wide', onClick: () => {
    deps.actions.send({ t: 'ready', ready: !(room.me?.ready ?? false) });
    deps.services.playUi('coin');
    deps.nav.back();
  } });
  const rematch = button({ label: 'REMATCH', icon: 'rematch', variant: 'mint', cls: 'wide', onClick: () => {
    deps.actions.send({ t: 'rematch' });
    deps.services.playUi('coin');
    deps.nav.back();
  } });
  const spectate = button({ label: 'SPECTATE', icon: 'eye', variant: 'sky', cls: 'wide', onClick: () => {
    deps.actions.spectate(!deps.services.spectating());
    deps.nav.back();
  } });
  const roomSettings = button({ label: 'ROOM SETTINGS', icon: 'sliders', cls: 'wide', onClick: () => deps.nav.show('createRoom', { edit: true }) });
  const start = button({ label: 'START NOW', icon: 'flag', variant: 'mint', cls: 'wide', onClick: () => {
    deps.actions.send({ t: 'start' });
    deps.nav.back();
  } });
  const locker = button({ label: 'LOCKER', icon: 'shirt', variant: 'grape', cls: 'wide', onClick: () => deps.nav.show('locker') });
  const settings = button({ label: 'SETTINGS', icon: 'gear', variant: 'cream', cls: 'wide', onClick: () => deps.nav.show('settings') });
  const howTo = button({ label: 'HOW TO PLAY', icon: 'question', variant: 'cream', cls: 'wide', onClick: () => deps.nav.show('howToPlay') });
  const copy = button({ label: 'COPY LINK', icon: 'link', variant: 'cream', cls: 'wide', onClick: async () => {
    const ok = await copyText(roomLink(room.code));
    setLabel(copy, ok ? 'LINK COPIED!' : 'COPY FAILED');
    deps.services.playUi(ok ? 'coin' : 'error');
  } });
  const restart = button({ label: 'RESTART', icon: 'rematch', variant: 'yellow', cls: 'wide', onClick: () => {
    deps.nav.back();
    deps.actions.restartPractice();
  } });
  const leave = button({ label: 'LEAVE', icon: 'exit', variant: 'danger', cls: 'wide pause-leave', onClick: () => deps.actions.leave() });
  const sub = el('p', { class: 'pause-sub' });
  const grid = el('div', { class: 'pause-grid' }, ready, rematch, spectate, start, roomSettings, restart, locker, settings, howTo, copy);
  const codeChip = el('span', { class: 'chip ink pause-code' });

  return {
    el: el('div', { class: 'screen modal pause' },
      el('div', { class: 'panel hero with-ribbon pause-panel springy' },
        el('div', { class: 'ribbon', text: 'PAUSED' }),
        el('div', { class: 'pause-head' }, sub, codeChip),
        resume,
        grid,
        leave)),
    onShow() {
      const inRoom = room.inRoom;
      const phase = room.phase;
      const v = deps.view();
      const meBean = v.localSlot < v.beans.length ? v.beans[v.localSlot] : null;
      setLabel(copy, 'COPY LINK');
      setHidden(copy, !inRoom);
      setHidden(codeChip, !inRoom);
      setText(codeChip, `ROOM ${room.code}`);
      setHidden(restart, inRoom);
      setHidden(locker, inRoom && phase !== RoomPhase.LOBBY);
      const lobby = inRoom && phase === RoomPhase.LOBBY && !room.spectator;
      setHidden(ready, !lobby);
      setLabel(ready, room.me?.ready ? 'NOT READY' : 'READY UP');
      setHidden(rematch, !(inRoom && phase === RoomPhase.PODIUM && !room.spectator));
      const host = inRoom && room.isHost && room.dto?.kind === 'private' && phase === RoomPhase.LOBBY;
      setHidden(roomSettings, !host);
      setHidden(start, !host);
      const ghost = inRoom && phase === RoomPhase.PLAY && meBean !== null && meBean.active && meBean.ghost;
      const watching = deps.services.spectating();
      setHidden(spectate, !(ghost || (watching && !room.spectator)));
      setLabel(spectate, watching ? 'STOP SPECTATING' : 'SPECTATE');
      setLabel(leave, inRoom ? 'LEAVE ROOM' : 'LEAVE PRACTICE');
      setText(sub, inRoom ? 'The game keeps going while you’re here!' : 'Practice keeps running behind this menu.');
      // Two columns: an odd one out spans the row, so the grid never shows a hole.
      const shown = [...grid.children].filter((n): n is HTMLElement => n instanceof HTMLElement && !n.hidden);
      shown.forEach((n, i) => n.classList.toggle('span2', i === shown.length - 1 && shown.length % 2 === 1));
    },
  };
}
